import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { clearToken, saveToken } from '@/app/shared/utils/tokenUtils';
import type { AccountInfo, TenantProfile } from '@azure/msal-browser';

export type SerializableAccountInfo =
  Omit<AccountInfo, 'tenantProfiles'> & {
    tenantProfiles?: Record<string, TenantProfile>;
  };
export interface AuthState {
  token: string | null;
  isLoading: boolean;
  errorStatus: number | null;
  errorMessage: string | null;
  isAuthenticated: boolean;
  email: string | null;
  profile?: SerializableAccountInfo | null;
  userProfile?: IUserProfile | null;
  authId?: string | null;
  contactId?: string | null;
  leadId?: string | null;
  assetId?: string | null;
  source?: string | null;
  stipsId?: string | null;
  metadataProcessed: boolean;
}

const initialState: AuthState = {
  token: null,
  isLoading: false,
  errorStatus: null,
  errorMessage: null,
  isAuthenticated: false,
  email: null,
  profile: null,
  userProfile: null,
  authId: null,
  contactId: null,
  leadId: null,
  assetId: null,
  source: null,
  stipsId: null,
  metadataProcessed: false,
};

export interface IUserProfile {
  firstName: string;
  lastName: string;
  emailId: string;
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setMetadataProcessed: (state, action: PayloadAction<boolean>) => {
      state.metadataProcessed = action.payload;
    },

    setMetadata: (
      state,
      action: PayloadAction<{ authId: string; contactId: string; leadId: string; assetId: string; source: string }>
    ) => {
      state.authId = action.payload.authId;
      state.contactId = action.payload.contactId;
      state.leadId = action.payload.leadId;
      state.assetId = action.payload.assetId;
      state.source = action.payload.source;
    },

    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      state.isAuthenticated = true;
      state.errorStatus = null;
      state.errorMessage = null;
      // Also save to localStorage
      saveToken(action.payload);
    },

    setEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload;
    },

    setProfile: (state, action: PayloadAction<SerializableAccountInfo>) => {
      state.profile = action.payload;
    },

    setUserProfile: (state, action: PayloadAction<IUserProfile>) => {
      state.userProfile = action.payload;
    },

    setStipsId: (state, action: PayloadAction<string | null>) => {
      state.stipsId = action.payload;
    },

    setContactId: (state, action: PayloadAction<string | null>) => {
      state.contactId = action.payload;
    },

    setError: (state, action: PayloadAction<{ status: number; message: string }>) => {
      state.errorStatus = action.payload.status;
      state.errorMessage = action.payload.message;
      state.isLoading = false;
    },

    // Clear error state
    clearError: (state) => {
      state.errorStatus = null;
      state.errorMessage = null;
    },

    logout: (state) => {
      state.token = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.errorStatus = null;
      state.errorMessage = null;
      state.email = null;
      state.profile = null;
      state.userProfile = null;
      state.contactId = null;
      state.metadataProcessed = false;
      // Clear token from localStorage
      clearToken();
    },

    // Reset entire state
    resetAuthState: () => initialState,
  },
});

export const { setLoading, setMetadataProcessed, setMetadata, setToken, setEmail, setProfile, setUserProfile, setContactId, setStipsId, setError, clearError, logout, resetAuthState } = authSlice.actions;

export default authSlice.reducer;
